import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
} from 'rxjs';
import { PermissionsService } from '@shared/services/permissions.service';
import { IrisService } from '@shared/services/iris.service';
import {
  PermissionGrants,
  StudyDetail,
  StudyTeam,
} from './interfaces/permissions.interface';
import { REGIONS } from './constants';
import { ERMDatePipe } from './pipes/erm-date.pipe';

@Injectable()
export class StudyDetailsFacadeService {
  private readonly _permissionsService = inject(PermissionsService);
  private readonly _irisService = inject(IrisService);
  private readonly _ermDatePipe = inject(ERMDatePipe);

  private studyDetailsSubject = new BehaviorSubject<any>(null);
  private workspacesSubject = new BehaviorSubject<any[]>([]);
  private selectedTabSubject = new BehaviorSubject<number>(0);

  get studyDetails$(): Observable<any> {
    return this.studyDetailsSubject.asObservable().pipe(
      switchMap(() =>
        combineLatest([
          this.studyDetailsSubject.asObservable(),
          this._permissionsService.isInternal$,
        ]).pipe(
          mergeMap(([studyDetails, isInternal]) => {
            if (!studyDetails?.id) {
              return of({
                canEdit: false,
                canRemove: false,
                canViewContact: false,
                isInternal,
                studyDetails,
              });
            }
            const canViewContact$ =
              this._permissionsService.observeEntityPermissions(
                studyDetails.id,
                [StudyTeam],
                [PermissionGrants.CanView],
              );

            const canEdit$ = this._permissionsService.observeEntityPermissions(
              studyDetails.id,
              [StudyTeam, StudyDetail],
              [PermissionGrants.CanEdit],
            );

            const canRemove$ =
              this._permissionsService.observeEntityPermissions(
                studyDetails.id,
                [StudyTeam, StudyDetail],
                [PermissionGrants.CanRemove],
              );

            return combineLatest({
              canEdit: canEdit$,
              canRemove: canRemove$,
              canViewContact: canViewContact$,
              isInternal: of(isInternal),
              studyDetails: of(studyDetails),
            });
          }),
        ),
      ),
    );
  }

  get workspaces$(): Observable<any[]> {
    return this.workspacesSubject.asObservable();
  }

  get selectedTab$(): Observable<number> {
    return this.selectedTabSubject.asObservable();
  }

  loadStudyDetailFn(studyId: string) {
    return this._irisService.getStudyDetailById(studyId);
  }

  loadStudyDetails(studyId: string): void {
    this.loadStudyDetailFn(studyId)
      .pipe(take(1))
      .subscribe((study) => {
        if (study) {
          study.study = study;
          study.regions = [];

          if (
            study.irb_approval_dt ||
            study.oversight?.includes(REGIONS.US) ||
            study.oversight?.includes(REGIONS.US_CAN)
          ) {
            study.regions.push({
              name: 'US / International',
              status: study.irb_status,
              irb_approval_dt: study.irb_approval_dt,
              irb_expiration_dt: study.irb_approval_expiry_dt,
              irb_closure_dt: study.irb_closure_dt,
            });
          }

          if (
            study.can_irb_approval_dt ||
            study.oversight?.includes(REGIONS.CAN) ||
            study.oversight?.includes(REGIONS.US_CAN)
          ) {
            study.regions.push({
              name: 'Canada',
              status: study.can_irb_status,
              irb_approval_dt: study.can_irb_approval_dt,
              irb_expiration_dt: study.can_irb_approval_expiry_dt,
              irb_closure_dt: study.can_irb_closure_dt,
            });
          }

            // Set permissions for the entity
          if (study?.role) {
            this._permissionsService.setEntityRolesAndPermission(
              study.id,
              study.role,
            );
          }


          const croOrg = study.organization?.find(
            (org: any) => org.type === 'CRO',
          );
          if (croOrg) {
            study.details = study.details || [];
            study.details.push({ label: 'CRO:', value: croOrg.name });
          }

          if (study.irb_status?.toLowerCase() === 'closed') {
            study.irb_status = `${study.irb_status} (${this._ermDatePipe.transform(study.irb_closure_dt) || ''})`;
          }

          this.studyDetailsSubject.next(study);
        }
      });
  }

  setSelectedTab(index: number): void {
    this.selectedTabSubject.next(index);
  }
}
